import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import {
  getFounder, updateFounder, uploadTeamMemberPhoto,
  getAgents, addAgent, updateAgent, deleteAgent
} from './services/teamStructureService';

// Mirrors the color tokens defined in App.js's module-scope `T` object (not exported
// there, so mirrored here rather than widening App.js's export surface).
const T = {
  bg: '#050810', card: '#080d16', border: '#0f1929', borderMid: '#1a2a3a',
  text: '#c0d0e0', textMid: '#8090a0', textDim: '#4a6080', textFaint: '#1a2a3a', mono: 'monospace',
};
const label = { fontSize:10, color:T.textDim, letterSpacing:3, fontFamily:T.mono, marginBottom:8, display:'block' };
const inp = { width:'100%', background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:'10px 14px', color:T.text, fontSize:14, boxSizing:'border-box', fontFamily:'inherit', outline:'none' };
const inlineText = (fontSize, fontWeight=600) => ({
  background:'transparent', border:'1px solid transparent', borderRadius:6, padding:'3px 8px',
  color:T.text, fontSize, fontWeight, fontFamily:'inherit', outline:'none', textAlign:'center', width:'100%', boxSizing:'border-box',
});
// Frosted-glass card shell — translucent bg + backdrop blur + inset highlight, in place
// of the flat opaque T.card panels used elsewhere in the app.
const glassCard = (accentColor, radius = 16) => ({
  position:'relative', background:'rgba(9,15,26,0.58)', backdropFilter:'blur(18px) saturate(150%)',
  WebkitBackdropFilter:'blur(18px) saturate(150%)', borderRadius:radius, border:`1px solid ${accentColor}35`,
  boxShadow:`inset 0 1px 0 rgba(255,255,255,0.05), 0 0 24px ${accentColor}12`, overflow:'hidden',
});
const withCacheBust = (url, v) => !url ? url : `${url}${url.includes('?') ? '&' : '?'}v=${v || 0}`;

// Dashboard's signature accent (sidebar highlight / streak pill color) — the founder's default.
const ACCENT = '#F5A623';
// Default agent accents, cycled at creation time — the exact palette already used across
// SEED_BUSINESSES / CRM_STAGE_COLORS in App.js. Persisted per-agent as `accentColor` once set
// (via the color picker) or assigned, so a color stays tied to an agent rather than shifting
// when other agents are added/removed.
const AGENT_COLORS = ['#3498DB', '#2ECC71', '#9B59B6', '#E74C3C', '#F5A623', '#1ABC9C'];
const CARD_MIN_W = 200, CARD_MAX_W = 240;

const SOCIAL_PLATFORMS = [
  { id:'linkedin', label:'LinkedIn', color:'#0A66C2' },
  { id:'x', label:'X', color:'#e8e8e8' },
  { id:'instagram', label:'Instagram', color:'#E1306C' },
  { id:'github', label:'GitHub', color:'#c0c0c0' },
  { id:'website', label:'Website', color:ACCENT },
  { id:'whatsapp', label:'WhatsApp', color:'#25D366' },
  { id:'telegram', label:'Telegram', color:'#2AABEE' },
  { id:'other', label:'Other', color:T.textDim },
];
const socialMeta = (platform) => SOCIAL_PLATFORMS.find(p => p.id === platform) || SOCIAL_PLATFORMS[SOCIAL_PLATFORMS.length - 1];

function SocialIcon({ platform, size = 14 }) {
  const s = { width:size, height:size, viewBox:'0 0 16 16' };
  // Brand-color logo marks (WhatsApp, Telegram) carry their own fixed fill/gradient
  // rather than currentColor, unlike the other hand-drawn glyphs below, so they read as
  // the real platform logo regardless of the chip's accent tint.
  const gradId = useId();
  switch (platform) {
    case 'linkedin': return <svg {...s} fill="currentColor"><rect x="1.3" y="5.3" width="2.6" height="9.4"/><circle cx="2.6" cy="2.3" r="1.6"/><path d="M6.3 5.3h2.6v1.5c.6-1 1.7-1.7 3.1-1.7 2.3 0 3.2 1.5 3.2 3.9v5.7h-2.6V9.5c0-1-.4-1.8-1.4-1.8-.9 0-1.5.6-1.5 1.8v5.2H6.3V5.3z"/></svg>;
    case 'x': return <svg {...s} fill="currentColor"><path d="M2 2l4.6 6.2L2.2 14h1.7l3.8-4.5L10.9 14H14l-4.9-6.6L13.4 2h-1.7L8.1 6.1 5 2H2z"/></svg>;
    case 'instagram': return <svg {...s} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="12" height="12" rx="3.5"/><circle cx="8" cy="8" r="2.8"/><circle cx="11.6" cy="4.4" r="0.6" fill="currentColor" stroke="none"/></svg>;
    case 'github': return <svg {...s} fill="currentColor"><path d="M8 1a7 7 0 00-2.2 13.6c.35.07.48-.15.48-.34v-1.3c-1.95.42-2.36-.83-2.36-.83-.32-.8-.78-1.02-.78-1.02-.63-.44.05-.43.05-.43.7.05 1.07.72 1.07.72.62 1.07 1.63.76 2.03.58.06-.45.24-.76.44-.94-1.56-.18-3.2-.78-3.2-3.47 0-.77.27-1.4.72-1.89-.07-.18-.31-.9.07-1.87 0 0 .59-.19 1.94.72a6.7 6.7 0 013.53 0c1.35-.91 1.94-.72 1.94-.72.38.97.14 1.69.07 1.87.45.49.72 1.12.72 1.89 0 2.7-1.65 3.29-3.22 3.46.25.22.48.65.48 1.31v1.94c0 .19.13.42.49.34A7 7 0 008 1z"/></svg>;
    case 'whatsapp': return <svg width={size} height={size} viewBox="0 0 360 362" fill="none"><path fill="#25D366" fillRule="evenodd" clipRule="evenodd" d="M307.546 52.566C273.709 18.684 228.706.017 180.756 0 81.951 0 1.538 80.404 1.504 179.235c-.017 31.594 8.242 62.432 23.928 89.609L0 361.736l95.024-24.925c26.179 14.285 55.659 21.805 85.655 21.814h.077c98.788 0 179.21-80.413 179.244-179.244.017-47.898-18.608-92.926-52.454-126.807v-.008Zm-126.79 275.788h-.06c-26.73-.008-52.952-7.194-75.831-20.765l-5.44-3.231-56.391 14.791 15.05-54.981-3.542-5.638c-14.912-23.721-22.793-51.139-22.776-79.286.035-82.14 66.867-148.973 149.051-148.973 39.793.017 77.198 15.53 105.328 43.695 28.131 28.157 43.61 65.596 43.593 105.398-.035 82.149-66.867 148.982-148.982 148.982v.008Zm81.719-111.577c-4.478-2.243-26.497-13.073-30.606-14.568-4.108-1.496-7.09-2.243-10.073 2.243-2.982 4.487-11.568 14.577-14.181 17.559-2.613 2.991-5.226 3.361-9.704 1.117-4.477-2.243-18.908-6.97-36.02-22.226-13.313-11.878-22.304-26.54-24.916-31.027-2.613-4.486-.275-6.91 1.959-9.136 2.011-2.011 4.478-5.234 6.721-7.847 2.244-2.613 2.983-4.486 4.478-7.469 1.496-2.991.748-5.603-.369-7.847-1.118-2.243-10.073-24.289-13.812-33.253-3.636-8.732-7.331-7.546-10.073-7.692-2.613-.13-5.595-.155-8.586-.155-2.991 0-7.839 1.118-11.947 5.604-4.108 4.486-15.677 15.324-15.677 37.361s16.047 43.344 18.29 46.335c2.243 2.991 31.585 48.225 76.51 67.632 10.684 4.615 19.029 7.374 25.535 9.437 10.727 3.412 20.49 2.931 28.208 1.779 8.604-1.289 26.498-10.838 30.228-21.298 3.73-10.46 3.73-19.433 2.613-21.298-1.117-1.865-4.108-2.991-8.586-5.234l.008-.017Z"/></svg>;
    case 'telegram': return (
      <svg width={size} height={size} viewBox="0 0 256 256">
        <defs>
          <linearGradient id={gradId} x1="50%" x2="50%" y1="0%" y2="100%">
            <stop offset="0%" stopColor="#2AABEE"/>
            <stop offset="100%" stopColor="#229ED9"/>
          </linearGradient>
        </defs>
        <path fill={`url(#${gradId})`} d="M128 0C94.06 0 61.48 13.494 37.5 37.49A128.038 128.038 0 0 0 0 128c0 33.934 13.5 66.514 37.5 90.51C61.48 242.506 94.06 256 128 256s66.52-13.494 90.5-37.49c24-23.996 37.5-56.576 37.5-90.51 0-33.934-13.5-66.514-37.5-90.51C194.52 13.494 161.94 0 128 0Z"/>
        <path fill="#FFF" d="M57.94 126.648c37.32-16.256 62.2-26.974 74.64-32.152 35.56-14.786 42.94-17.354 47.76-17.441 1.06-.017 3.42.245 4.96 1.49 1.28 1.05 1.64 2.47 1.82 3.467.16.996.38 3.266.2 5.038-1.92 20.24-10.26 69.356-14.5 92.026-1.78 9.592-5.32 12.808-8.74 13.122-7.44.684-13.08-4.912-20.28-9.63-11.26-7.386-17.62-11.982-28.56-19.188-12.64-8.328-4.44-12.906 2.76-20.386 1.88-1.958 34.64-31.748 35.26-34.45.08-.338.16-1.598-.6-2.262-.74-.666-1.84-.438-2.64-.258-1.14.256-19.12 12.152-54 35.686-5.1 3.508-9.72 5.218-13.88 5.128-4.56-.098-13.36-2.584-19.9-4.708-8-2.606-14.38-3.984-13.82-8.41.28-2.304 3.46-4.662 9.52-7.072Z"/>
      </svg>
    );
    case 'website': return <svg {...s} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="6.3"/><path d="M1.7 8h12.6M8 1.7c1.7 1.8 2.7 4 2.7 6.3S9.7 12.5 8 14.3C6.3 12.5 5.3 10.3 5.3 8S6.3 3.5 8 1.7z"/></svg>;
    default: return <svg {...s} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 9.5l3-3M6.3 4.5H4.3a3 3 0 000 6h2M9.7 4.5h2a3 3 0 010 6h-2"/></svg>;
  }
}

function useDebouncedSave(delay = 600) {
  const timers = useRef({});
  useEffect(() => () => { Object.values(timers.current).forEach(clearTimeout); }, []);
  return useCallback((key, fn) => {
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(fn, delay);
  }, [delay]);
}

function IconButton({ onClick, title, color, hoverColor, children, chip }) {
  const [hover, setHover] = useState(false);
  return (
    <button onClick={onClick} title={title} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ background: chip ? 'rgba(0,0,0,0.45)' : 'transparent', backdropFilter: chip ? 'blur(4px)' : 'none', border:'none', cursor:'pointer', color: hover ? hoverColor : color, padding: chip ? '5px 6px' : '3px 5px', borderRadius: chip ? '50%' : 4, lineHeight:1, display:'flex', alignItems:'center', transition:'color 0.15s' }}>
      {children}
    </button>
  );
}
const pencilIcon = <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm17.71-10.21a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>;
const trashIcon = <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zm13-15h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>;
const checkIcon = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l6 6L20 6"/></svg>;

// Reuses ClientCard's native `<input type="color">` picker mechanism (AddClientModal's
// "CARD COLOR" field), restyled as a compact circular chip so it fits the card's icon-button
// row instead of the modal's 44px box + separate preview swatch.
function ColorSwatch({ value, onChange, size = 20, title }) {
  return (
    <label title={title} style={{ position:'relative', display:'block', width:size, height:size, borderRadius:'50%', background:value, border:'2px solid rgba(255,255,255,0.4)', boxShadow:'0 1px 4px rgba(0,0,0,0.5)', cursor:'pointer', flexShrink:0 }}>
      <input type="color" value={value} onChange={e => onChange(e.target.value)} style={{ position:'absolute', inset:0, opacity:0, width:'100%', height:'100%', border:'none', padding:0, cursor:'pointer' }}/>
    </label>
  );
}

// Exported photo size — the crop modal always renders down to this regardless of source
// image size or on-screen preview size.
const PHOTO_SIZE = 1080;
const CROP_PREVIEW = 320; // on-screen square preview size (px); scaled up to PHOTO_SIZE on export

// Drag-to-reposition cropper: loads the picked file, scales it to always cover a square
// preview, lets the user pan it (bounded so the square is never under-filled), and exports
// the current pan/zoom as a fixed PHOTO_SIZE x PHOTO_SIZE JPEG blob via canvas.
function PhotoCropModal({ file, accent, onCancel, onConfirm }) {
  const [img, setImg] = useState(null);
  const [baseScale, setBaseScale] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const minDim = Math.min(image.width, image.height);
      setBaseScale(CROP_PREVIEW / minDim);
      setImg(image);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    };
    image.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const clamp = (ox, oy, z) => {
    if (!img) return { x: 0, y: 0 };
    const w = img.width * baseScale * z, h = img.height * baseScale * z;
    const maxX = Math.max(0, (w - CROP_PREVIEW) / 2);
    const maxY = Math.max(0, (h - CROP_PREVIEW) / 2);
    return { x: Math.min(maxX, Math.max(-maxX, ox)), y: Math.min(maxY, Math.max(-maxY, oy)) };
  };

  const onPointerDown = e => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, origin: offset };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = e => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX, dy = e.clientY - dragRef.current.startY;
    setOffset(clamp(dragRef.current.origin.x + dx, dragRef.current.origin.y + dy, zoom));
  };
  const onPointerUp = () => { dragRef.current = null; };

  const onZoomChange = v => {
    const z = Number(v);
    setZoom(z);
    setOffset(prev => clamp(prev.x, prev.y, z));
  };

  const handleSave = () => {
    if (!img) return;
    setSaving(true);
    const canvas = document.createElement('canvas');
    canvas.width = PHOTO_SIZE; canvas.height = PHOTO_SIZE;
    const ctx = canvas.getContext('2d');
    const exportScale = PHOTO_SIZE / CROP_PREVIEW;
    const w = img.width * baseScale * zoom * exportScale;
    const h = img.height * baseScale * zoom * exportScale;
    const dx = PHOTO_SIZE / 2 - w / 2 + offset.x * exportScale;
    const dy = PHOTO_SIZE / 2 - h / 2 + offset.y * exportScale;
    ctx.drawImage(img, dx, dy, w, h);
    canvas.toBlob(blob => { setSaving(false); if (blob) onConfirm(blob); }, 'image/jpeg', 0.9);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'#000000cc', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:T.card, borderRadius:14, padding:24, width:380, maxWidth:'92vw', border:`1px solid ${accent}40` }}>
        <h3 style={{ fontSize:15, fontWeight:700, color:T.text, marginBottom:4 }}>Reposition Photo</h3>
        <p style={{ fontSize:12, color:T.textDim, marginBottom:14 }}>Drag to reposition, zoom to resize. Exports as {PHOTO_SIZE}×{PHOTO_SIZE}.</p>
        <div
          onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
          style={{ width:CROP_PREVIEW, height:CROP_PREVIEW, margin:'0 auto', borderRadius:12, overflow:'hidden', position:'relative', cursor: dragRef.current ? 'grabbing' : 'grab', border:`1px solid ${accent}50`, background:T.bg, touchAction:'none' }}>
          {img && (
            <img src={img.src} alt="" draggable={false} style={{
              position:'absolute',
              left: CROP_PREVIEW / 2 - (img.width * baseScale * zoom) / 2 + offset.x,
              top: CROP_PREVIEW / 2 - (img.height * baseScale * zoom) / 2 + offset.y,
              width: img.width * baseScale * zoom, height: img.height * baseScale * zoom,
              userSelect:'none', pointerEvents:'none', maxWidth:'none',
            }}/>
          )}
        </div>
        <input type="range" min="1" max="3" step="0.01" value={zoom} onChange={e => onZoomChange(e.target.value)} style={{ width:'100%', marginTop:14, accentColor:accent }}/>
        <div style={{ display:'flex', gap:10, marginTop:18 }}>
          <button onClick={handleSave} disabled={!img || saving} style={{ background:accent, border:'none', borderRadius:8, padding:12, color:'#000', fontWeight:700, fontSize:14, cursor:'pointer', flex:1, opacity: (!img || saving) ? 0.5 : 1 }}>{saving ? 'Saving...' : 'Save Photo'}</button>
          <button onClick={onCancel} style={{ padding:'12px 20px', background:T.bg, color:T.textDim, border:`1px solid ${T.border}`, borderRadius:8, cursor:'pointer', fontSize:14 }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function SocialsEditor({ socials, onChange, accent }) {
  // All three actions call the same onChange — the parent debounces every socials write
  // under one key regardless of which action triggered it (see commitFounderSocials /
  // commitAgentSocials), so there's no separate debounce-key logic needed here anymore.
  const update = (i, patch) => onChange(socials.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const remove = (i) => onChange(socials.filter((_, idx) => idx !== i));
  const add = () => onChange([...socials, { platform:'website', url:'' }]);
  return (
    <div>
      <label style={label}>SOCIAL LINKS</label>
      <div style={{ display:'grid', gap:6 }}>
        {socials.map((s, i) => (
          <div key={i} style={{ display:'flex', gap:6 }}>
            <select value={s.platform} onChange={e => update(i, { platform:e.target.value })} style={{ ...inp, flex:'0 0 108px', fontSize:12, padding:'8px 10px' }}>
              {SOCIAL_PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <input value={s.url} onChange={e => update(i, { url:e.target.value })} placeholder="https://..." style={{ ...inp, flex:1, fontSize:12, padding:'8px 10px' }}/>
            <button onClick={() => remove(i)} style={{ background:'#1a0808', border:'1px solid #E74C3C30', color:'#E74C3C', borderRadius:8, padding:'0 10px', cursor:'pointer', fontSize:14, flexShrink:0 }}>✕</button>
          </div>
        ))}
        <button onClick={add} style={{ background:'transparent', border:`1px dashed ${T.border}`, color:T.textDim, borderRadius:8, padding:'7px', cursor:'pointer', fontSize:12, textAlign:'left' }}>+ Add Social Link</button>
      </div>
    </div>
  );
}

// Purely presentational — takes finished path data and draws it. No refs, no effects, no
// measurement of its own. All positions come from the single computeLinePaths pass in the
// parent (see TeamStructureView).
function ConnectorLines({ paths }) {
  return (
    <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', overflow:'visible', pointerEvents:'none', zIndex:2 }}>
      <defs>
        {/* gradientUnits="userSpaceOnUse" with each path's own actual start/end coordinates —
            the default objectBoundingBox normalizes x1/x2 as fractions of the shape's own
            bounding box, and a perfectly (or near-perfectly) vertical path has zero bounding-
            box width, which is a degenerate case the SVG spec doesn't define a sane result
            for — browsers commonly resolve it by not painting the gradient at all, which for
            a stroke paint server means no stroke, i.e. an invisible line despite a fully
            valid path. userSpaceOnUse uses real coordinates instead, so a zero-width bbox
            never enters the calculation. */}
        {paths.map(({ i, startColor, endColor, gx1, gy1, gx2, gy2 }) => (
          <linearGradient key={i} id={`tsLineGrad${i}`} gradientUnits="userSpaceOnUse" x1={gx1} y1={gy1} x2={gx2} y2={gy2}>
            <stop offset="0%" stopColor={startColor} stopOpacity="0.95"/>
            <stop offset="100%" stopColor={endColor} stopOpacity="0.7"/>
          </linearGradient>
        ))}
      </defs>
      {paths.map(({ i, d, endColor }) => (
        <path key={i} d={d} fill="none" stroke={`url(#tsLineGrad${i})`} strokeWidth="2.5" strokeDasharray="4 5" className="ts-flow-line"
          style={{ filter:`drop-shadow(0 0 4px ${endColor}90)` }}/>
      ))}
    </svg>
  );
}

function AgentCard({ agent, color, editing, onToggleEdit, onField, onSocials, onPhoto, uploading, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);
  const bio = agent.bio || '';
  const bioLong = bio.length > 90;
  const initials = (agent.name || '').split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'AI';

  return (
    <div className="ts-card-in"
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ ...glassCard(color, 16), borderTop:`2px solid ${color}`, transform: hovered ? 'translateY(-3px)' : 'translateY(0)', boxShadow: hovered ? `inset 0 1px 0 rgba(255,255,255,0.05), 0 10px 28px -8px ${color}45, 0 0 30px ${color}20` : `inset 0 1px 0 rgba(255,255,255,0.05), 0 0 24px ${color}12`, transition:'transform 0.18s ease, box-shadow 0.18s ease' }}>
      {editing && (
        <div style={{ position:'absolute', top:8, left:8, zIndex:2 }}>
          <ColorSwatch value={color} onChange={v => onField('accentColor', v)} title="Card accent color"/>
        </div>
      )}
      <div style={{ position:'absolute', top:8, right:8, display:'flex', gap:2, zIndex:2 }}>
        <IconButton chip onClick={onToggleEdit} title={editing ? 'Done editing' : 'Edit agent'} color={editing ? color : '#e8eef7'} hoverColor={color}>
          {editing ? checkIcon : pencilIcon}
        </IconButton>
        <IconButton chip onClick={onDelete} title="Delete agent" color="#f0a0a0" hoverColor="#E74C3C">
          {trashIcon}
        </IconButton>
      </div>

      <label style={{ position:'relative', display:'block', width:'100%', paddingTop:'100%', cursor:'pointer', overflow:'hidden' }}>
        {agent.photoURL
          ? <img src={withCacheBust(agent.photoURL, agent.updatedAt)} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover' }}/>
          : <div style={{ position:'absolute', inset:0, background:`linear-gradient(160deg, ${color}22, ${color}08)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'2.2rem', fontWeight:700, color }}>{initials}</div>}
        {uploading && <div style={{ position:'absolute', inset:0, background:'#000000a8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, color:'#fff', fontFamily:T.mono, letterSpacing:1 }}>UPLOADING...</div>}
        <input type="file" accept="image/*" style={{ display:'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) onPhoto(f); e.target.value=''; }}/>
      </label>

      <div style={{ padding:'10px 14px 12px' }}>
        <div style={{ textAlign:'center', marginBottom:14 }}>
          {editing
            ? <input className="ts-inline-input" value={agent.name} onChange={e => onField("name", e.target.value)} placeholder="Agent name" autoComplete="off" style={inlineText(15, 700)}/>
            : <div style={{ fontSize:15, fontWeight:700, color:T.text }}>{agent.name || 'Unnamed Agent'}</div>}
          {editing
            ? <input className="ts-inline-input" value={agent.role} onChange={e => onField("role", e.target.value)} placeholder="Role" autoComplete="off" style={{ ...inlineText(12, 600), color, marginTop:2 }}/>
            : <div style={{ fontSize:12, fontWeight:600, color, marginTop:2 }}>{agent.role || '—'}</div>}
        </div>

        {editing ? (
          <textarea className="ts-inline-input" value={bio} onChange={e => onField('bio', e.target.value)} placeholder="Short description..." style={{ ...inp, resize:'vertical', minHeight:60, fontSize:12, marginBottom:14 }}/>
        ) : bio ? (
          <p onClick={() => bioLong && setExpanded(x => !x)} style={{ fontSize:12, color:T.textMid, lineHeight:1.5, marginBottom:14, cursor: bioLong ? 'pointer' : 'default', display: expanded ? 'block' : '-webkit-box', WebkitLineClamp: expanded ? 'unset' : 2, WebkitBoxOrient:'vertical', overflow: expanded ? 'visible' : 'hidden' }}>
            {bio}{bioLong && <span style={{ color, marginLeft:4, fontSize:11, fontWeight:600 }}>{expanded ? 'less' : 'more'}</span>}
          </p>
        ) : null}

        {editing ? (
          <SocialsEditor socials={agent.socials || []} onChange={onSocials} accent={color}/>
        ) : (agent.socials || []).length > 0 && (
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {agent.socials.map((s, i) => {
              const meta = socialMeta(s.platform);
              if (!s.url) return null;
              const href = /^https?:\/\//.test(s.url) ? s.url : `https://${s.url}`;
              return (
                <a key={i} href={href} target="_blank" rel="noopener noreferrer" title={meta.label}
                  style={{ width:26, height:26, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', background:`${meta.color}18`, border:`1px solid ${meta.color}35`, color:meta.color, flexShrink:0 }}>
                  <SocialIcon platform={s.platform} size={12}/>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const css = `
.ts-inline-input{ transition:background 0.15s, border-color 0.15s; }
.ts-inline-input:hover{ border-color:${T.border}; background:${T.bg}; }
.ts-inline-input:focus{ border-color:${ACCENT}60; background:${T.bg}; }
/* Browser autofill (e.g. Chrome recognizing the founder name field as a "name" input)
   force-overrides background/text color regardless of autoComplete="off" in many cases —
   this neutralizes it so it can't break the dark glass look with a solid white box. */
.ts-inline-input:-webkit-autofill,
.ts-inline-input:-webkit-autofill:hover,
.ts-inline-input:-webkit-autofill:focus {
  -webkit-text-fill-color: ${T.text};
  -webkit-box-shadow: 0 0 0px 1000px ${T.bg} inset;
  box-shadow: 0 0 0px 1000px ${T.bg} inset;
  transition: background-color 5000s ease-in-out 0s;
}
.ts-founder-pulse{ animation: tsPulse 3.2s ease-in-out infinite; }
@keyframes tsPulse {
  0%,100% { box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 0 40px var(--ts-founder-glow, ${ACCENT}20); }
  50% { box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 0 58px var(--ts-founder-glow-strong, ${ACCENT}38), 0 0 90px ${ACCENT}12; }
}
.ts-card-in{ animation: tsFadeIn 0.35s ease both; }
@keyframes tsFadeIn { from{ opacity:0; transform:translateY(8px); } to{ opacity:1; transform:translateY(0); } }
.ts-flow-line{ animation: tsDash 1.2s linear infinite; }
@keyframes tsDash { to { stroke-dashoffset:-18; } }
`;

export default function TeamStructureView({ showNotif }) {
  const [founder, setFounder] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [agents, setAgents] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [uploadingFounderPhoto, setUploadingFounderPhoto] = useState(false);
  const [uploadingAgentPhoto, setUploadingAgentPhoto] = useState(null);
  const [editingAgentId, setEditingAgentId] = useState(null);
  const [cropTarget, setCropTarget] = useState(null); // { docId: 'founder' | agentId, file }
  const [linePaths, setLinePaths] = useState([]);

  const scheduleSave = useDebouncedSave();

  // Extracted so the effect below has plain variables in its dependency array (an inline
  // expression there trips the exhaustive-deps lint rule, which can't statically verify it).
  const agentAccentColorsKey = agents.map(a => a.accentColor || '').join(',');
  const founderAccentColor = founder && founder.accentColor;

  // --- Connector-line measurement: ONE wrapper ref, ONE ResizeObserver, ONE function
  // that writes linePaths. See the effect below for the only two places it's invoked from.
  const containerRef = useRef(null);
  const founderCardRef = useRef(null);
  const agentCardRefs = useRef([]);
  // Cache of per-index ref-setter functions, created once per index and reused forever
  // after. `ref={el => { agentCardRefs.current[i] = el; }}` written directly inside the
  // .map() below was a NEW function on every render — React detaches (calls with null)
  // and reattaches every card's ref on every parent re-render, not just on mount. This
  // app has onSnapshot listeners in App.js (businesses/byforce clients, invoices, leads)
  // that fire independently of which view is active and cascade a re-render down into
  // this un-memoized component; if the ResizeObserver's guaranteed initial callback landed
  // inside one of those detach/reattach gaps, that card's ref would read null at exactly
  // the wrong moment. getAgentRefSetter(i) returns the SAME function for a given index
  // across every render, so there's nothing left to detach/reattach after mount.
  const agentRefSetters = useRef([]);
  const getAgentRefSetter = (i) => {
    if (!agentRefSetters.current[i]) {
      agentRefSetters.current[i] = (el) => { agentCardRefs.current[i] = el; };
    }
    return agentRefSetters.current[i];
  };
  // Kept in sync every render (not via an effect) purely so the measurement function —
  // which only ever runs from inside the ResizeObserver callback — always reads current
  // data without needing agents/founder in that effect's dependency array (which would
  // tear down and recreate the observer on every keystroke while editing a field).
  const agentsRef = useRef(agents); agentsRef.current = agents;
  const founderRef = useRef(founder); founderRef.current = founder;

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loaded) return;
    const container = containerRef.current;
    if (!container) return;

    function computeLinePaths() {
      const founderEl = founderCardRef.current;
      if (!founderEl) { setLinePaths([]); return; }
      const currentAgents = agentsRef.current;
      const currentFounder = founderRef.current;
      const cRect = container.getBoundingClientRect();
      const fRect = founderEl.getBoundingClientRect();
      const startX = fRect.left + fRect.width / 2 - cRect.left;
      const startY = fRect.bottom - cRect.top;
      const startColor = currentFounder?.accentColor || ACCENT;
      const next = [];
      agentCardRefs.current.slice(0, currentAgents.length).forEach((el, i) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const endX = r.left + r.width / 2 - cRect.left;
        const endY = r.top - cRect.top;
        const midY = startY + (endY - startY) / 2;
        const endColor = currentAgents[i]?.accentColor || AGENT_COLORS[i % AGENT_COLORS.length];
        next.push({ i, d:`M${startX},${startY} C${startX},${midY} ${endX},${midY} ${endX},${endY}`, startColor, endColor, gx1:startX, gy1:startY, gx2:endX, gy2:endY });
      });
      setLinePaths(next);
    }

    let raf = null;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(computeLinePaths);
    });
    // Watching the single wrapper is enough: it changes size whenever anything inside it
    // does — an agent card added/removed, the grid wrapping to a new row, the window
    // resizing. ResizeObserver also fires once immediately on `.observe()`, which covers
    // the initial paint with no polling needed.
    ro.observe(container);

    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
    // Re-armed (not "re-triggered" by a separate mechanism — the same observer just gets
    // reattached, and its guaranteed initial-fire recomputes immediately) when a color
    // changes, since a color change doesn't resize anything and so wouldn't otherwise reach
    // computeLinePaths. Agent count/position changes need no entry here — the persistent
    // observer already catches those because they always change the wrapper's own size.
  }, [loaded, founderAccentColor, agentAccentColorsKey]);

  async function load() {
    try {
      const [f, a] = await Promise.all([getFounder(), getAgents()]);
      setFounder(f || { name:'', role:'', photoURL:'', bio:'', socials:[], accentColor:ACCENT });
      setAgents(a);
    } catch (e) { showNotif && showNotif('Error loading team structure', 'error'); }
    setLoaded(true);
  }

  function updateFounderField(field, value) {
    setFounder(prev => ({ ...prev, [field]: value }));
    scheduleSave(`founder-${field}`, async () => {
      try { await updateFounder({ [field]: value }); }
      catch (e) { showNotif && showNotif('Error saving founder info', 'error'); }
    });
  }

  // Every socials mutation (add, remove, platform change, URL typing) goes through the
  // SAME debounce key, so there's only ever one pending write for the array at a time.
  // Previously add/platform-change wrote immediately while URL typing debounced — two
  // independent in-flight writes to the same document with no ordering guarantee between
  // them, so a blank-URL "add" write could resolve after a typed-URL write and silently
  // clobber it. Blank rows are filtered out at save time only (not from local state, so
  // an in-progress empty row still shows while editing) so they never persist.
  function commitFounderSocials(next) {
    setFounder(prev => ({ ...prev, socials: next }));
    scheduleSave('founder-socials', async () => {
      try { await updateFounder({ socials: next.filter(s => s.url && s.url.trim()) }); }
      catch (e) { showNotif && showNotif('Error saving founder info', 'error'); }
    });
  }

  async function handleCropConfirm(blob) {
    const { docId } = cropTarget;
    setCropTarget(null);
    if (docId === 'founder') {
      setUploadingFounderPhoto(true);
      try {
        const url = await uploadTeamMemberPhoto('founder', blob);
        const stamp = Date.now();
        setFounder(prev => ({ ...prev, photoURL: url, updatedAt: stamp }));
        await updateFounder({ photoURL: url });
        showNotif && showNotif('Founder photo updated!');
      } catch (e) { showNotif && showNotif('Error uploading photo', 'error'); }
      setUploadingFounderPhoto(false);
    } else {
      setUploadingAgentPhoto(docId);
      try {
        const url = await uploadTeamMemberPhoto(docId, blob);
        const stamp = Date.now();
        setAgents(prev => prev.map(a => a.id === docId ? { ...a, photoURL: url, updatedAt: stamp } : a));
        await updateAgent(docId, { photoURL: url });
        showNotif && showNotif('Photo updated!');
      } catch (e) { showNotif && showNotif('Error uploading photo', 'error'); }
      setUploadingAgentPhoto(null);
    }
  }

  function updateAgentField(agentId, field, value) {
    setAgents(prev => prev.map(a => a.id === agentId ? { ...a, [field]: value } : a));
    scheduleSave(`agent-${agentId}-${field}`, async () => {
      try { await updateAgent(agentId, { [field]: value }); }
      catch (e) { showNotif && showNotif('Error saving agent', 'error'); }
    });
  }

  function commitAgentSocials(agentId, next) {
    setAgents(prev => prev.map(a => a.id === agentId ? { ...a, socials: next } : a));
    scheduleSave(`agent-${agentId}-socials`, async () => {
      try { await updateAgent(agentId, { socials: next.filter(s => s.url && s.url.trim()) }); }
      catch (e) { showNotif && showNotif('Error saving agent', 'error'); }
    });
  }

  async function handleAddAgent() {
    try {
      const accentColor = AGENT_COLORS[agents.length % AGENT_COLORS.length];
      const created = await addAgent({ name:'', role:'', photoURL:'', bio:'', socials:[], accentColor });
      setAgents(prev => [...prev, created]);
      setEditingAgentId(created.id);
    } catch (e) { showNotif && showNotif('Error adding agent', 'error'); }
  }

  async function handleDeleteAgent(agentId) {
    try {
      await deleteAgent(agentId);
      setAgents(prev => prev.filter(a => a.id !== agentId));
      if (editingAgentId === agentId) setEditingAgentId(null);
      showNotif && showNotif('Agent deleted!');
    } catch (e) { showNotif && showNotif('Error deleting agent', 'error'); }
    setDeleteConfirm(null);
  }

  if (!loaded) return <div style={{ textAlign:'center', color:T.textDim, padding:60, fontFamily:T.mono, letterSpacing:2 }}>LOADING TEAM STRUCTURE...</div>;

  const founderColor = founder.accentColor || ACCENT;
  const agentColors = agents.map((a, i) => a.accentColor || AGENT_COLORS[i % AGENT_COLORS.length]);

  return (
    // Palantir-style ambient background — reuses AnalysisView's exact grid-line +
    // radial-vignette treatment (src/App.js ~line 1314) rather than inventing new CSS.
    <div style={{ position:'relative', margin:-24, padding:24, background:T.bg, backgroundImage:`linear-gradient(${T.border} 1px, transparent 1px), linear-gradient(90deg, ${T.border} 1px, transparent 1px), radial-gradient(ellipse at 50% 0%, ${founderColor}08 0%, transparent 60%)`, backgroundSize:'40px 40px, 40px 40px, 100% 100%', minHeight:'100%' }}>
      <style>{css}</style>
      <div style={{ marginBottom:32 }}>
        <div style={{ fontSize:11, color:T.textDim, letterSpacing:3, fontFamily:T.mono, marginBottom:4 }}>ORG CHART</div>
        <h2 style={{ fontSize:20, fontWeight:700, color:T.text }}>Team Structure</h2>
        <p style={{ fontSize:13, color:T.textDim, marginTop:4 }}>The founder, and the AI agents running the mission.</p>
      </div>

      <div ref={containerRef} style={{ position:'relative' }}>
        <ConnectorLines paths={linePaths}/>

        {/* 90px gap (was 44px) — confirmed root cause of the "missing" connector line was
            never a bug: a near-vertical connector to a card sitting almost directly under
            the founder only had ~44px to render in, and got visually swallowed by the
            founder/card glow at that length. 90px gives every connector, however close to
            vertical, enough length to read clearly as a line regardless of where any given
            agent card ends up relative to the founder — fixes this for any future position,
            not just this specific layout. */}
        <div style={{ position:'relative', zIndex:1, display:'flex', justifyContent:'center', marginBottom:90 }}>
          <div ref={founderCardRef} className="ts-founder-pulse" style={{ ...glassCard(founderColor, 20), width:300, maxWidth:'92vw', borderTop:`3px solid ${founderColor}`, '--ts-founder-glow':`${founderColor}20`, '--ts-founder-glow-strong':`${founderColor}38` }}>
            <div style={{ position:'absolute', top:8, left:8, zIndex:2 }}>
              <ColorSwatch value={founderColor} onChange={v => updateFounderField('accentColor', v)} title="Card accent color"/>
            </div>

            <label style={{ position:'relative', display:'block', width:'100%', paddingTop:'100%', cursor:'pointer', overflow:'hidden' }}>
              {founder.photoURL
                ? <img src={withCacheBust(founder.photoURL, founder.updatedAt)} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover' }}/>
                : <div style={{ position:'absolute', inset:0, background:`linear-gradient(160deg, ${founderColor}25, ${founderColor}08)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'2.8rem' }}>⚡</div>}
              {uploadingFounderPhoto && <div style={{ position:'absolute', inset:0, background:'#000000a8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, color:'#fff', fontFamily:T.mono, letterSpacing:1 }}>UPLOADING...</div>}
              <input type="file" accept="image/*" style={{ display:'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) setCropTarget({ docId:'founder', file:f }); e.target.value=''; }}/>
            </label>

            <div style={{ padding:'12px 20px 16px', textAlign:'center' }}>
              <div style={{ fontSize:9, color:T.textDim, letterSpacing:3, fontFamily:T.mono, marginBottom:12 }}>FOUNDER · THE BRAIN</div>
              <input className="ts-inline-input" value={founder.name} onChange={e => updateFounderField('name', e.target.value)} placeholder="Fahd" autoComplete="off" style={inlineText(20, 700)}/>
              <input className="ts-inline-input" value={founder.role} onChange={e => updateFounderField('role', e.target.value)} placeholder="Founder" autoComplete="off" style={{ ...inlineText(13, 600), color:founderColor, marginTop:2 }}/>
              <div style={{ marginTop:14, textAlign:'left' }}>
                <SocialsEditor socials={founder.socials || []} onChange={commitFounderSocials} accent={founderColor}/>
              </div>
            </div>
          </div>
        </div>

        {/* Centered as a group via flexbox + wrap — any number of cards that fit in one row
            stay centered; once they'd overflow the row width, they wrap, no JS agent-count
            threshold involved. */}
        <div style={{ position:'relative', zIndex:1, display:'flex', flexWrap:'wrap', justifyContent:'center', gap:20 }}>
          {agents.map((agent, i) => (
            <div key={agent.id} ref={getAgentRefSetter(i)} style={{ flex:`0 1 ${CARD_MAX_W}px`, minWidth:CARD_MIN_W, maxWidth:CARD_MAX_W }}>
              <AgentCard agent={agent} color={agentColors[i]}
                editing={editingAgentId === agent.id}
                onToggleEdit={() => setEditingAgentId(prev => prev === agent.id ? null : agent.id)}
                onField={(field, value) => updateAgentField(agent.id, field, value)}
                onSocials={next => commitAgentSocials(agent.id, next)}
                onPhoto={file => setCropTarget({ docId: agent.id, file })}
                uploading={uploadingAgentPhoto === agent.id}
                onDelete={() => setDeleteConfirm(agent.id)}
              />
            </div>
          ))}
          <div style={{ flex:`0 1 ${CARD_MAX_W}px`, minWidth:CARD_MIN_W, maxWidth:CARD_MAX_W }}>
            <button onClick={handleAddAgent} style={{ width:'100%', height:'100%', minHeight:170, background:'rgba(9,15,26,0.4)', backdropFilter:'blur(10px)', WebkitBackdropFilter:'blur(10px)', border:`1px dashed ${T.borderMid}`, color:T.textDim, borderRadius:16, cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, fontSize:13, fontFamily:'inherit' }}>
              <span style={{ fontSize:26 }}>＋</span>
              <span>Add Agent</span>
            </button>
          </div>
        </div>
      </div>

      {cropTarget && (
        <PhotoCropModal
          file={cropTarget.file}
          accent={cropTarget.docId === 'founder' ? founderColor : (agentColors[agents.findIndex(a => a.id === cropTarget.docId)] || ACCENT)}
          onCancel={() => setCropTarget(null)}
          onConfirm={handleCropConfirm}
        />
      )}

      {deleteConfirm && (
        <div style={{ position:'fixed', inset:0, background:'#000000cc', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:T.card, borderRadius:14, padding:28, width:380, maxWidth:'92vw', border:'1px solid #E74C3C40' }}>
            <h3 style={{ fontSize:16, fontWeight:700, color:T.text, marginBottom:10 }}>Delete Agent?</h3>
            <p style={{ fontSize:14, color:T.textDim, marginBottom:20, lineHeight:1.6 }}>Are you sure you want to delete this agent? This will also delete their photo. This can't be undone.</p>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => handleDeleteAgent(deleteConfirm)} style={{ background:'#E74C3C', border:'none', borderRadius:8, padding:12, color:'#000', fontWeight:700, fontSize:14, cursor:'pointer', flex:1 }}>Delete</button>
              <button onClick={() => setDeleteConfirm(null)} style={{ padding:'12px 20px', background:T.bg, color:T.textDim, border:`1px solid ${T.border}`, borderRadius:8, cursor:'pointer', fontSize:14 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
